/**
 * 통관정보 — 좌 목차 + 우 카테고리 내용(요건 유형별 세번 목록).
 *   URL: /?audit=req-guide&cat=customs_check&hsk=8471300000 (딥링크)
 *   데이터=customs_hs_master 요건 JSONB(관세청 007Q + law.go.kr) 재사용, 원문=관세청 CLIP.
 *   ※ CLHS 무복사 — 구조 참고만, loginHS 정체성 구현.
 */
import { useEffect, useState } from "react";
import {
  Megaphone,
  Files,
  BadgeCheck,
  Hash,
  Globe,
  ShieldAlert,
  Scale,
  FileMinus,
} from "lucide-react";
import LawGuide, { LawGuideContent } from "./LawGuide";
import HubGrid from "./HubGrid";
import { ReqLines } from "./reqTextFormat";
import { AgencyLink, LawLink, TelLink, normalizeLawName, openLink, hskDetailUrl } from "./reqLinks";
import AgencyContactList from "./AgencyContactList";
import "./theme.css";

const CATS = [
  { key: "customs_check", label: "세관장확인", active: true },
  { key: "integrated_notice", label: "통합공고", active: true },
  { key: "customs_notice", label: "수출입공고", active: true },
  { key: "origin", label: "원산지", active: true },
  { key: "strategic", label: "전략물자", active: true },
  { key: "by_hsk", label: "HS별 신고가이드", active: true },
  { key: "req_by_law", label: "법령별 통관정보", active: true },
  { key: "approval_exempt", label: "수출입승인의 면제", active: true },
];

// 카드 허브(랜딩)용 아이콘·색·설명 — 관세 제도 안내와 동일 카드 구성.
const CAT_META = {
  customs_notice: { icon: Megaphone, accent: "#b45309", desc: "대외무역법상 수출입을 제한·금지하거나 승인 대상으로 지정·고시한 것." },
  integrated_notice: { icon: Files, accent: "#0369a1", desc: "여러 개별 법령의 수출입 요건·절차를 통합해 고시한 것." },
  customs_check: { icon: BadgeCheck, accent: "#047857", desc: "통관 시 세관장이 허가·승인·표시 등 요건 구비를 확인(관세법 제226조)." },
  by_hsk: { icon: Hash, accent: "#4338ca", desc: "세번(HSK)을 입력해 해당 물품의 수출입 요건을 조회." },
  origin: { icon: Globe, accent: "#7c3aed", desc: "원산지표시 대상·표시방법(대외무역법 제33조)." },
  strategic: { icon: ShieldAlert, accent: "#b91c1c", desc: "전략물자 수출통제 대상·판정 정보." },
  req_by_law: { icon: Scale, accent: "#0f766e", desc: "요건 법령별로 대상 세번을 조회." },
  approval_exempt: { icon: FileMinus, accent: "#6b7280", desc: "수출입 승인이 면제되는 대상." },
};

function readUrl() {
  const p = new URLSearchParams(window.location.search);
  return { cat: p.get("cat") || "", hsk: p.get("hsk") || "", law: p.get("law") || "" };
}
function goCat(cat) {
  const p = new URLSearchParams(window.location.search);
  p.set("audit", "req-guide");
  p.set("cat", cat);
  p.delete("hsk");
  window.history.pushState({}, "", "/?" + p.toString());
  window.dispatchEvent(new PopStateEvent("popstate"));
}
function goDetail(hsk) { openLink(hskDetailUrl(hsk)); } // 65차: 설정(새 탭|같은 탭)대로
function fmtHsk(s) {
  const d = String(s || "").replace(/\D/g, "");
  if (d.length <= 4) return d;
  if (d.length <= 6) return `${d.slice(0, 4)}.${d.slice(4)}`;
  return `${d.slice(0, 4)}.${d.slice(4, 6)}-${d.slice(6)}`;
}

export function isMeaningfulStrategicText(value) {
  const normalized = String(value || "")
    .trim()
    .toLowerCase()
    .replace(/[.!]+$/, "")
    .trim();
  return Boolean(normalized) && ![
    "there were no results found",
    "no results found",
    "no result found",
  ].includes(normalized);
}

export function formatStrategicSummary(value) {
  return String(value || "")
    .split("·")
    .map((part) => part.trim().replace(/^전략물자\s*/i, ""))
    .filter(Boolean)
    .join(" · ");
}

const pgBtn = {
  border: "1px solid var(--audit-border)",
  background: "var(--audit-bg)",
  color: "var(--audit-text)",
  borderRadius: 6,
  padding: "5px 12px",
  fontSize: 12,
  cursor: "pointer",
  fontFamily: "inherit",
};

export default function ReqGuidePage() {
  const [sel, setSel] = useState(readUrl);
  const [counts, setCounts] = useState({});
  useEffect(() => {
    const h = () => setSel(readUrl());
    window.addEventListener("popstate", h);
    return () => window.removeEventListener("popstate", h);
  }, []);
  useEffect(() => {
    fetch("/api/onto/req-guide/counts")
      .then((r) => (r.ok ? r.json() : {}))
      .then(setCounts)
      .catch(() => setCounts({}));
  }, []);

  // 카테고리 미선택 = 통관정보 카드 허브(관세 제도 안내와 동일 구성). 좌측 레일은
  //   사이드바 아코디언으로 대체됨 → 여기선 제거하고 허브/내용만 렌더.
  if (!sel.cat) {
    const cards = CATS.map((c) => {
      const m = CAT_META[c.key] || {};
      return {
        id: c.key,
        icon: m.icon,
        title: c.label,
        desc: m.desc,
        accent: m.accent,
        count: counts[c.key],
        onClick: () => goCat(c.key),
      };
    });
    return (
      <HubGrid
        title="통관정보"
        subtitle="수출입 요건·통관 관련 정보를 유형별로 확인하세요. 수출입공고·통합공고·세관장확인·원산지·전략물자 등 대상 세번과 근거 법령을 안내합니다."
        cards={cards}
      />
    );
  }

  const active = CATS.find((c) => c.key === sel.cat) || CATS[0];
  return (
    <div
      className="audit-root"
      style={{
        height: "100%",
        overflow: "auto",
        background: "var(--audit-bg)",
        padding: "16px 22px",
      }}
    >
      {!active.active ? (
        <div>
          <h2 style={{ fontSize: 16, fontWeight: 700 }}>{active.label}</h2>
          <div style={{ fontSize: 12.5, color: "var(--audit-text-muted)", marginTop: 8 }}>
            준비중입니다. (Phase 2 — 관세청 고시·공식 API 확보 후 제공)
          </div>
        </div>
      ) : (
        <CategoryPanel key={active.key} cat={active.key} label={active.label} initialHsk={sel.hsk} initialLaw={sel.law} />
      )}
    </div>
  );
}

function CategoryPanel({ cat, label, initialHsk, initialLaw }) {
  const [q, setQ] = useState("");
  const [page, setPage] = useState(0);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [openHsk, setOpenHsk] = useState(initialHsk || null);
  const [tab, setTab] = useState(initialHsk ? "hsk" : "laws"); // 조회형 공고: 대상법령 vs 세번찾기

  useEffect(() => { setPage(0); setOpenHsk(initialHsk || null); }, [cat, initialHsk]);

  useEffect(() => {
    if (cat === "by_hsk" || STATIC_GUIDE_LAW[cat]) return; // 세번 입력형·정적 제도 카테고리
    setLoading(true);
    const u = `/api/onto/req-guide/list?cat=${cat}&q=${encodeURIComponent(q)}&page=${page}&size=30`;
    fetch(u)
      .then((r) => (r.ok ? r.json() : { items: [] }))
      .then((d) => setItems(Array.isArray(d.items) ? d.items : []))
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  }, [cat, q, page]);

  if (cat === "by_hsk") return <ByHskPanel initialHsk={initialHsk} />;
  if (cat === "req_by_law") return <LawListPanel initialLaw={initialLaw} />;
  if (STATIC_GUIDE_LAW[cat]) return <StaticGuidePanel cat={cat} label={label} />;

  const isReqType =
    cat === "customs_notice" || cat === "integrated_notice" || cat === "customs_check";
  const displayLabel = cat === "strategic" ? "전략물자 HSK 연계정보" : label;

  const hskSearch = (
    <div>
      <input
        value={q}
        onChange={(e) => { setQ(e.target.value); setPage(0); }}
        placeholder="세번(4자리+) 또는 품명 검색"
        style={{ width: "min(360px,90%)", padding: "7px 10px", fontSize: 12.5, borderRadius: 7,
                 border: "1px solid var(--audit-border)", background: "var(--audit-bg)",
                 color: "var(--audit-text)", margin: "0 0 12px", boxSizing: "border-box" }}
      />
      {loading ? (
        <div style={{ fontSize: 12.5, color: "var(--audit-text-muted)" }}>불러오는 중…</div>
      ) : items.length === 0 ? (
        <div style={{ fontSize: 12.5, color: "var(--audit-text-muted)" }}>해당 요건 세번이 없습니다.</div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
          {items.map((it) => (
            <div key={it.hsk}>
              <button
                onClick={() =>
                  cat === "origin"
                    ? goDetail(it.hsk)
                    : setOpenHsk(openHsk === it.hsk ? null : it.hsk)
                }
                style={{ display: "flex", gap: 10, alignItems: "flex-start", width: "100%", textAlign: "left",
                         border: "none", borderBottom: "1px solid var(--audit-border)", background: "transparent",
                         padding: "8px 4px", cursor: "pointer", fontFamily: "inherit" }}
              >
                <span style={{ fontFamily: "JetBrains Mono, monospace", fontSize: 13, color: "var(--audit-accent)", fontWeight: 700 }}>
                  {fmtHsk(it.hsk)}
                </span>
                <span style={{ fontSize: 13.5, color: "var(--audit-text)", flex: 1, minWidth: 0 }}>
                  <span style={{ display: "block" }}>{it.name_ko}</span>
                  {cat === "strategic" && it.summary && (
                    <span style={{ display: "block", marginTop: 4, fontSize: 12, lineHeight: 1.45, color: "var(--audit-text-muted)" }}>
                      연계 통제번호 <b style={{ color: "var(--audit-text)" }}>{formatStrategicSummary(it.summary)}</b>
                    </span>
                  )}
                </span>
                {it.side && (
                  <span style={{ fontSize: 11, padding: "2px 7px", borderRadius: 999, flexShrink: 0,
                                 border: cat === "strategic" ? "1px solid #f59e0b" : "none",
                                 background: cat === "strategic" ? "#fffbeb" : "var(--audit-bg-subtle)",
                                 color: cat === "strategic" ? "#92400e" : "var(--audit-text-muted)" }}>
                    {cat === "strategic" ? "판정 필요" : it.side}
                  </span>
                )}
              </button>
              {cat !== "origin" && openHsk === it.hsk && <HskReqDetail hsk={it.hsk} cat={cat} item={it} />}
            </div>
          ))}
          <div style={{ display: "flex", gap: 8, marginTop: 10 }}>
            <button disabled={page === 0} onClick={() => setPage((p) => Math.max(0, p - 1))} style={pgBtn}>이전</button>
            <span style={{ fontSize: 12, color: "var(--audit-text-muted)", alignSelf: "center" }}>{page + 1} 페이지</span>
            <button disabled={items.length < 30} onClick={() => setPage((p) => p + 1)} style={pgBtn}>다음</button>
          </div>
        </div>
      )}
    </div>
  );

  if (!isReqType) {
    return (
      <div>
        <h2 style={{ fontSize: 16, fontWeight: 700, marginBottom: 8 }}>{displayLabel}</h2>
        {REQ_TYPE_INTRO[cat] && <div style={introCard}>{REQ_TYPE_INTRO[cat]}</div>}
        {cat === "origin" && (
          <div style={{ marginTop: 8 }}>
            <LawGuide law="원산지 표시(대외무역법 제33조)" />
          </div>
        )}
        <div style={{ marginTop: 14 }}>{hskSearch}</div>
      </div>
    );
  }

  return (
    <div>
      <h2 style={{ fontSize: 16, fontWeight: 700, marginBottom: 8 }}>{label}</h2>
      {REQ_TYPE_INTRO[cat] && <div style={introCard}>{REQ_TYPE_INTRO[cat]}</div>}
      <div style={{ display: "flex", gap: 4, borderBottom: "1px solid var(--audit-border)", margin: "14px 0 16px" }}>
        <button onClick={() => setTab("laws")} style={subTabStyle(tab === "laws")}>대상 법령</button>
        <button onClick={() => setTab("hsk")} style={subTabStyle(tab === "hsk")}>세번으로 찾기</button>
      </div>
      {tab === "laws" ? <LawTablePanel cat={cat} /> : hskSearch}
    </div>
  );
}

// 공고 유형별 제도 개요(큐레이트·정확) — CLHS 무복사, 근거 법령 명시.
const REQ_TYPE_INTRO = {
  customs_notice:
    "수출입공고는 「대외무역법」 제11조·제12조에 따라 산업통상자원부장관이 수출입을 제한·금지하거나 승인 대상으로 지정·고시한 것입니다. 아래는 이 공고와 연계된 요건 법령이며, 법령을 선택하면 AI 가이드로 요건·절차를 확인할 수 있습니다.",
  integrated_notice:
    "통합공고는 「대외무역법」 제12조에 따라 여러 개별 법령의 수출입 요건·절차를 산업통상자원부장관이 한데 모아 고시한 것입니다. 아래는 통합공고로 연계된 요건 법령입니다.",
  customs_check:
    "세관장확인은 「관세법」 제226조에 따라 수출입 시 허가·승인·표시 등 요건 구비 여부를 세관장이 통관 단계에서 확인하는 제도입니다. 아래는 세관장확인 대상 요건 법령입니다.",
  origin:
    "원산지표시는 「대외무역법」 제33조와 대외무역관리규정에 따라, 원산지표시대상물품을 수입할 때 최종 구매자가 원산지를 오인하지 않도록 표시하게 하는 제도입니다. 보통 한글·한자·영문으로 「원산지: 국가명」 형식으로 잘 보이는 곳에 쉽게 지워지지 않게 표시하며, 견본·개인용 등 일부는 면제됩니다. 아래는 세번별 원산지표시 대상(Y) 여부이며, 세번을 누르면 표시방법 등 상세로 이동합니다.",
  strategic:
    "아래 목록은 YesTrade의 HSK–통제번호 연계정보를 세번 기준으로 정리한 판정 후보입니다. HSK가 연계되어 있다는 사실만으로 전략물자로 확정되지 않으며, 제품의 성능·사양·재질·용도와 전략물자 수출입고시의 기술적 통제기준을 대조한 자가판정 또는 전문판정이 필요합니다.",
  approval_exempt:
    "수출입승인의 면제는 「대외무역법 시행령」 제19조에 따라, 원칙적으로 수출입승인 대상인 물품이라도 외화획득용 원료·기재, 무상 반출입, 긴급·특정 목적 수출입 등 일정 사유에 해당하면 승인을 면제받는 제도입니다.",
};

// 정적 제도 카테고리 → AI 가이드 생성 키(근거 법령/고시). law-guide 재사용.
const STATIC_GUIDE_LAW = {
  approval_exempt: "수출입승인의 면제(대외무역법 시행령 제19조)",
};

// 공고 유형의 대상 법령 표(마스터-디테일) — 좌 법령 리스트(세번 수 순) / 우 AI 가이드.
function LawTablePanel({ cat }) {
  const [laws, setLaws] = useState(null);
  const [sel, setSel] = useState(null);
  const [q, setQ] = useState("");
  useEffect(() => {
    setLaws(null);
    setSel(null);
    fetch(`/api/onto/req-guide/laws?cat=${cat}`)
      .then((r) => (r.ok ? r.json() : { laws: [] }))
      .then((d) => setLaws(Array.isArray(d.laws) ? d.laws : []))
      .catch(() => setLaws([]));
  }, [cat]);
  if (laws === null)
    return <div style={{ fontSize: 12.5, color: "var(--audit-text-muted)" }}>불러오는 중…</div>;
  const ql = q.trim();
  const filtered = ql
    ? laws.filter((l) => (l.law || "").includes(ql) || (l.ministry || "").includes(ql))
    : laws;
  const selLaw = laws.find((l) => l.law === sel);
  return (
    <div style={{ display: "flex", gap: 20, alignItems: "flex-start" }}>
      <div
        style={{
          width: 300, flexShrink: 0, position: "sticky", top: 0,
          maxHeight: "calc(100vh - 240px)", overflowY: "auto", paddingRight: 4,
        }}
      >
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="법령·소관부처 검색"
          style={{ width: "100%", padding: "7px 10px", fontSize: 12.5, borderRadius: 7,
                   border: "1px solid var(--audit-border)", background: "var(--audit-bg)",
                   color: "var(--audit-text)", margin: "0 0 6px", boxSizing: "border-box" }}
        />
        <div style={{ fontSize: 11, color: "var(--audit-text-muted)", margin: "0 0 6px" }}>
          대상 법령 {filtered.length}개 · 세번 많은 순
        </div>
        {filtered.map((l) => (
          <button key={l.law} onClick={() => setSel(l.law)} style={lawRow(sel === l.law)}>
            <div style={{ display: "flex", justifyContent: "space-between", gap: 8 }}>
              <span style={{ fontSize: 12.5, fontWeight: sel === l.law ? 700 : 500 }}>{l.law}</span>
              <span style={{ fontSize: 11, color: "var(--audit-text-faint)", flexShrink: 0 }}>{l.count}</span>
            </div>
            {l.ministry && (
              <div style={{ fontSize: 11, color: "var(--audit-text-muted)", marginTop: 1 }}>
                {l.ministry}{l.tel ? ` · ${l.tel}` : ""}
              </div>
            )}
          </button>
        ))}
      </div>
      <div style={{ flex: 1, minWidth: 0, borderLeft: "1px solid var(--audit-border)", paddingLeft: 20 }}>
        {selLaw ? (
          <div>
            <div style={{ fontSize: 16, fontWeight: 800, color: "var(--audit-text)" }}>{selLaw.law}</div>
            <div style={{ fontSize: 12, color: "var(--audit-text-muted)", margin: "3px 0 12px" }}>
              소관 {selLaw.ministry || "—"}{selLaw.tel ? ` · ${selLaw.tel}` : ""} · 관련 세번 {selLaw.count}
            </div>
            <AgencyContactList contacts={selLaw.contacts} />
            <LawGuideContent key={sel} law={sel} />
          </div>
        ) : (
          <div style={{ fontSize: 13, color: "var(--audit-text-muted)", padding: "48px 0", textAlign: "center" }}>
            왼쪽에서 법령을 선택하면 AI 가이드가 표시됩니다.
          </div>
        )}
      </div>
    </div>
  );
}

const introCard = {
  fontSize: 12.5,
  lineHeight: 1.7,
  color: "var(--audit-text)",
  background: "var(--audit-bg-subtle, var(--audit-bg-elevated))",
  border: "1px solid var(--audit-border)",
  borderRadius: 8,
  padding: "12px 14px",
};
const subTabStyle = (active) => ({
  border: "none",
  borderBottom: active ? "2px solid var(--audit-accent)" : "2px solid transparent",
  background: "transparent",
  color: active ? "var(--audit-text)" : "var(--audit-text-muted)",
  fontWeight: active ? 700 : 500,
  fontSize: 13,
  padding: "7px 10px 9px",
  marginBottom: -1,
  cursor: "pointer",
  fontFamily: "inherit",
});

function HskReqDetail({ hsk, cat, item }) {
  const [data, setData] = useState(null);
  useEffect(() => {
    let alive = true;
    fetch(`/api/onto/hsk-detail?hsk=${hsk}`)
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => alive && setData(d))
      .catch(() => alive && setData(null));
    return () => { alive = false; };
  }, [hsk]);
  if (!data) return <div style={{ fontSize: 12, color: "var(--audit-text-muted)", padding: "6px 4px 12px" }}>불러오는 중…</div>;
  if (cat === "strategic") return <StrategicHskDetail hsk={hsk} data={data} item={item} />;
  const req = data.requirements || {};
  const rows = [
    ...(req.import || []).map((x) => ["수입", x]),
    ...(req.export || []).map((x) => ["수출", x]),
  ];
  return (
    <div style={{ padding: "6px 4px 14px 4px", borderBottom: "1px solid var(--audit-border)" }}>
      {rows.length === 0 ? (
        <div style={{ fontSize: 12, color: "var(--audit-text-muted)" }}>요건 상세 없음</div>
      ) : (
        rows.map(([side, x], i) => (
          <div key={i} style={{ fontSize: 13.5, lineHeight: 1.75, marginBottom: 10 }}>
            <b style={{ color: x.label === "세관장확인" ? "#c0392b" : x.label === "통합공고" ? "#1d4ed8" : "var(--audit-text-muted)" }}>
              [{side}] {x.label}
            </b>
            <ReqLines text={x.text} laws={(x.agencies || []).map((a) => a.law).filter(Boolean)} style={{ marginTop: 3 }} />
            {Array.isArray(x.agencies) && x.agencies.length > 0 && (
              <div style={{ marginTop: 4, fontSize: 12.5, color: "var(--audit-text-muted)", display: "flex", flexWrap: "wrap", gap: "0 10px" }}>
                {x.agencies.map((a, j) => (
                  <span key={j} style={{ display: "inline-flex", gap: 5, alignItems: "baseline" }}>
                    {a.law && <LawLink law={a.law} muted />}
                    {a.ministry && <AgencyLink ministry={a.ministry} style={{ color: "var(--audit-text-muted)" }} />}
                    {a.tel && <TelLink tel={a.tel} />}
                  </span>
                ))}
              </div>
            )}
          </div>
        ))
      )}
      {(() => {
        const laws = [];
        rows.forEach(([, x]) =>
          (x.agencies || []).forEach((a) => {
            if (a.law && !laws.includes(a.law)) laws.push(a.law);
          }),
        );
        return laws.length ? (
          <div style={{ marginTop: 6 }}>
            {laws.map((law) => (
              <LawGuide key={law} law={law} />
            ))}
          </div>
        ) : null;
      })()}
      <div style={{ display: "flex", gap: 8, marginTop: 10 }}>
        <button onClick={() => goDetail(hsk)} style={pgBtn}>세번 상세로 →</button>
        <a href="https://unipass.customs.go.kr/clip/index.do" target="_blank" rel="noreferrer" style={{ ...pgBtn, textDecoration: "none" }}>
          관세청 원문 ↗
        </a>
      </div>
    </div>
  );
}

function StrategicHskDetail({ hsk, data, item }) {
  const raw = Array.isArray(item?.strategic_controls)
    ? item.strategic_controls
    : (Array.isArray(data?.requirements?.strategic) ? data.requirements.strategic : []);
  const controls = raw.filter(isMeaningfulStrategicText).map(formatStrategicSummary);
  const facts = [
    "정확한 모델명과 제품 사양서",
    "성능 수치·정밀도·용량·내환경성 등 기술 파라미터",
    "주요 재질·구성품·내장 소프트웨어",
    "최종 용도·최종 사용자·수출 목적지",
  ];
  return (
    <div style={{ padding: "12px 14px 16px", borderBottom: "1px solid var(--audit-border)", background: "#fffcf5" }}>
      <div style={{ display: "flex", justifyContent: "space-between", gap: 12, alignItems: "flex-start" }}>
        <div>
          <div style={{ fontSize: 11.5, fontWeight: 700, color: "#92400e" }}>현재 단계 · HSK 연계 후보</div>
          <div style={{ marginTop: 4, fontSize: 14, fontWeight: 800, color: "var(--audit-text)" }}>
            {fmtHsk(hsk)}에 연계된 통제번호
          </div>
        </div>
        <span style={{ fontSize: 11, border: "1px solid #f59e0b", color: "#92400e", borderRadius: 999, padding: "3px 8px" }}>
          전략물자 확정 아님
        </span>
      </div>
      <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginTop: 10 }}>
        {controls.length > 0 ? controls.map((control, i) => (
          <span key={`${control}-${i}`} style={{ fontFamily: "JetBrains Mono, monospace", fontSize: 12.5, fontWeight: 700,
                    background: "var(--audit-bg)", border: "1px solid var(--audit-border)", borderRadius: 6, padding: "5px 8px" }}>
            {control}
          </span>
        )) : <span style={{ fontSize: 12.5, color: "var(--audit-text-muted)" }}>유효한 연계 통제번호가 없습니다.</span>}
      </div>

      <div style={{ marginTop: 14, paddingTop: 12, borderTop: "1px solid #fde7b0" }}>
        <div style={{ fontSize: 12.5, fontWeight: 800, color: "var(--audit-text)" }}>판정 전에 확인할 제품 정보</div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(230px, 1fr))", gap: "5px 18px", marginTop: 7 }}>
          {facts.map((fact) => <div key={fact} style={{ fontSize: 12.5, color: "var(--audit-text-muted)" }}>· {fact}</div>)}
        </div>
      </div>

      <div style={{ marginTop: 12, fontSize: 12.5, lineHeight: 1.65, color: "var(--audit-text-muted)" }}>
        위 통제번호의 기술적 기준과 실제 제품 사양이 일치하는지 확인해야 합니다. 일치하면 자가판정을 등록하고,
        판단이 어렵거나 증빙이 필요한 경우 무역안보관리원 전문판정을 신청하세요.
      </div>
      <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginTop: 10 }}>
        <a href="https://www.yestrade.go.kr/hsk-control-search" target="_blank" rel="noreferrer" style={{ ...pgBtn, textDecoration: "none" }}>
          YesTrade HSK 연계표 ↗
        </a>
        <a href="https://www.yestrade.go.kr/judgements/self/find-controls" target="_blank" rel="noreferrer" style={{ ...pgBtn, textDecoration: "none" }}>
          온라인 자가판정 ↗
        </a>
        <button onClick={() => goDetail(hsk)} style={pgBtn}>세번 상세로 →</button>
      </div>
    </div>
  );
}

export function LawListPanel({ initialLaw }) {
  const [q, setQ] = useState("");
  const [agencies, setAgencies] = useState(null);
  const [sel, setSel] = useState(null);
  useEffect(() => {
    fetch("/api/onto/requirement-agencies")
      .then((r) => (r.ok ? r.json() : { agencies: [] }))
      .then((d) => setAgencies(Array.isArray(d.agencies) ? d.agencies : []))
      .catch(() => setAgencies([]));
  }, []);
  // 61차: 세번 상세의 법령 링크(?law=)로 들어오면 그 법령을 바로 선택한다 — 띄어쓰기·가운뎃점 차이는 같은 법령.
  useEffect(() => {
    if (!initialLaw || !Array.isArray(agencies)) return;
    const n = normalizeLawName(initialLaw);
    const hit = agencies.find((a) => normalizeLawName(a.law) === n);
    if (hit) setSel(hit.law);
    else setQ(initialLaw);
  }, [initialLaw, agencies]);
  if (agencies === null)
    return <div style={{ fontSize: 12.5, color: "var(--audit-text-muted)" }}>불러오는 중…</div>;
  const ql = q.trim();
  const filtered = ql
    ? agencies.filter((a) => (a.law || "").includes(ql) || (a.ministry || "").includes(ql))
    : agencies;
  const groups = {};
  filtered.forEach((a) => {
    const m = a.ministry || "기타";
    (groups[m] = groups[m] || []).push(a);
  });
  const ministries = Object.keys(groups).sort();
  const selAgency = agencies.find((a) => a.law === sel) || null;

  return (
    <div style={{ display: "flex", gap: 20, alignItems: "flex-start" }}>
      {/* 좌: 법령 리스트(소관부처 그룹) — 검정 위주, 선택만 강조 */}
      <div
        style={{
          width: 288, flexShrink: 0, position: "sticky", top: 0,
          maxHeight: "calc(100vh - 140px)", overflowY: "auto", paddingRight: 4,
        }}
      >
        <div style={{ fontSize: 15, fontWeight: 800, marginBottom: 8, color: "var(--audit-text)" }}>
          법령별 통관정보
        </div>
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="법령·소관부처 검색"
          style={{ width: "100%", padding: "7px 10px", fontSize: 12.5, borderRadius: 7,
                   border: "1px solid var(--audit-border)", background: "var(--audit-bg)",
                   color: "var(--audit-text)", margin: "0 0 10px", boxSizing: "border-box" }}
        />
        {ministries.length === 0 ? (
          <div style={{ fontSize: 12.5, color: "var(--audit-text-muted)" }}>일치하는 법령이 없습니다.</div>
        ) : (
          ministries.map((m) => (
            <div key={m} style={{ marginBottom: 8 }}>
              <div style={grpHead}>
                {m} <span style={{ color: "var(--audit-text-faint)", fontWeight: 500 }}>({groups[m].length})</span>
              </div>
              {groups[m].map((a) => (
                <button key={a.law} onClick={() => setSel(a.law)} style={lawRow(sel === a.law)}>
                  <div style={{ fontSize: 12.5, fontWeight: sel === a.law ? 700 : 500 }}>{a.law}</div>
                  {a.tel && (
                    <div style={{ fontSize: 11, color: "var(--audit-text-muted)", marginTop: 1 }}>{a.tel}</div>
                  )}
                </button>
              ))}
            </div>
          ))
        )}
      </div>

      {/* 우: 선택 법령 AI 가이드(펼쳐진 내용) */}
      <div style={{ flex: 1, minWidth: 0, borderLeft: "1px solid var(--audit-border)", paddingLeft: 20 }}>
        {selAgency ? (
          <div>
            <div style={{ fontSize: 16, fontWeight: 800, color: "var(--audit-text)" }}>{selAgency.law}</div>
            <div style={{ fontSize: 12, color: "var(--audit-text-muted)", margin: "3px 0 12px" }}>
              소관 {selAgency.ministry || "—"}{selAgency.tel ? ` · ${selAgency.tel}` : ""}
            </div>
            <AgencyContactList contacts={selAgency.contacts} />
            <LawGuideContent key={sel} law={sel} />
          </div>
        ) : (
          <div style={{ fontSize: 13, color: "var(--audit-text-muted)", padding: "48px 0", textAlign: "center" }}>
            왼쪽에서 법령을 선택하면 AI 가이드가 여기에 표시됩니다.
          </div>
        )}
      </div>
    </div>
  );
}

const grpHead = {
  fontSize: 11,
  fontWeight: 700,
  color: "var(--audit-text-muted)",
  margin: "10px 0 3px",
};
const lawRow = (active) => ({
  display: "block",
  width: "100%",
  textAlign: "left",
  border: "none",
  borderLeft: active ? "2px solid var(--audit-accent)" : "2px solid transparent",
  background: active ? "var(--audit-accent-bg)" : "transparent",
  color: "var(--audit-text)",
  padding: "6px 9px",
  cursor: "pointer",
  fontFamily: "inherit",
  borderRadius: "0 5px 5px 0",
});

// 정적 제도 카테고리(수출입승인 면제) — 제도 개요 + AI 가이드(자동 표시).
function StaticGuidePanel({ cat, label }) {
  const law = STATIC_GUIDE_LAW[cat];
  return (
    <div>
      <h2 style={{ fontSize: 16, fontWeight: 700, marginBottom: 8 }}>{label}</h2>
      {REQ_TYPE_INTRO[cat] && <div style={introCard}>{REQ_TYPE_INTRO[cat]}</div>}
      <div style={{ fontSize: 13, fontWeight: 700, color: "var(--audit-text)", margin: "16px 0 8px" }}>
        AI 가이드
      </div>
      <div style={{ padding: "12px 14px", border: "1px solid var(--audit-border)", borderRadius: 8, background: "var(--audit-bg-subtle, var(--audit-bg-elevated))", maxWidth: 820 }}>
        <LawGuideContent law={law} />
      </div>
    </div>
  );
}

function ByHskPanel({ initialHsk }) {
  const [hsk, setHsk] = useState(initialHsk || "");
  const [go, setGo] = useState(initialHsk || "");
  return (
    <div>
      <h2 style={{ fontSize: 16, fontWeight: 700, marginBottom: 8 }}>HS별 신고가이드</h2>
      <div style={{ display: "flex", gap: 8, marginBottom: 12 }}>
        <input
          value={hsk}
          onChange={(e) => setHsk(e.target.value.replace(/\D/g, ""))}
          placeholder="10자리 세번(HSK)"
          style={{ width: 200, padding: "7px 10px", fontSize: 12.5, borderRadius: 7,
                   border: "1px solid var(--audit-border)", background: "var(--audit-bg)", color: "var(--audit-text)" }}
        />
        <button onClick={() => setGo(hsk)} style={pgBtn}>조회</button>
      </div>
      {go && go.length >= 10 && <HskReqDetail hsk={go} />}
    </div>
  );
}
